import * as React from 'react';
import { useLayoutEffect } from 'react';
import styled from 'styled-components';

import Portal from '../Portal';
import Body03 from '../typography/Body03';

export interface TooltipPosition {
    left: number;
    top: number;
    shiftArrowLeft: boolean;
    shiftArrowRight: boolean;
}

export enum TooltipDirection {
    bottom,
    bottomRight,
    left,
    overlay,
    right,
    top,
    none
}

export interface BaseTooltipProps {
    className?: string;
    maxWidth?: string;
    direction: TooltipDirection;
    position: TooltipPosition | null;
    getTooltipRef?: React.RefObject<HTMLDivElement>;
    onLayout?(): void;
    visible: boolean;
}

const BaseTooltip: React.FC<React.PropsWithChildren<BaseTooltipProps>> = ({
    className,
    children,
    direction,
    getTooltipRef,
    maxWidth,
    onLayout,
    position,
    visible
}): JSX.Element | null => {
    useLayoutEffect(() => {
        if (onLayout) {
            onLayout();
        }
    }, []);

    if (!visible) {
        return null;
    }

    return (
        <Portal>
            <Root maxWidth={ maxWidth }
                ref={ getTooltipRef }
                style={ position || { opacity: 0 } }
            >
                <Text className={ className }>
                    { children }
                </Text>

                { direction === TooltipDirection.top &&
                <ArrowBottom shiftArrowLeft={ position !== null && position.shiftArrowLeft }
                    shiftArrowRight={ position !== null && position.shiftArrowRight }
                />
                }

                { (direction === TooltipDirection.bottom || direction === TooltipDirection.bottomRight) &&
                <ArrowTop shiftArrowLeft={ position !== null && position.shiftArrowLeft }
                    shiftArrowRight={ position !== null && position.shiftArrowRight }
                />
                }

                { direction === TooltipDirection.left &&
                    <ArrowLeft />
                }
            </Root>
        </Portal>
    );
};

export default BaseTooltip;

const Root = styled.div<{ maxWidth?: string }>`
    max-width: ${ ({ maxWidth, theme }) => (maxWidth ? maxWidth : theme.size.size_120) };
    position: absolute;
    z-index: 1000;
`;

const Text = styled(Body03)`
    ${ ({ theme }) => `
        background-color: ${ theme.color.grey.grey_90 };
        border-radius: ${ theme.radius.radius_2 };
        color: ${ theme.color.white };
        padding: ${ theme.size.size_5 } ${ theme.size.size_10 };
        position: relative;
    ` }
`;

const getArrowLeftPosition = ({ theme, shiftArrowLeft, shiftArrowRight }: any) => {
    if (shiftArrowLeft) {
        return `calc(${ theme.size.size_10 } - ${ theme.size.size_5 } / 2)`;
    }

    if (shiftArrowRight) {
        return `calc(100% - ${ theme.size.size_20 } + ${ theme.size.size_5 } / 2)`;
    }

    return `calc(50% - ${ theme.size.size_5 })`;
};

interface ArrowProps {
    shiftArrowLeft: boolean;
    shiftArrowRight: boolean;
}

const ArrowBottom = styled.span<ArrowProps>`
  ${ ({ theme }) => `
        border-left: ${ theme.size.size_5 } solid transparent;
        border-right: ${ theme.size.size_5 } solid transparent;
        border-top: ${ theme.size.size_5 } solid ${ theme.color.grey.grey_90 };
        bottom: -${ theme.size.size_5 };
        height: 0;
        position: absolute;
        width: 0;
    ` }

  left: ${ getArrowLeftPosition };
`;

const ArrowTop = styled.span<ArrowProps>`
  ${ ({ theme }) => `
        border-bottom: ${ theme.size.size_5 } solid ${ theme.color.grey.grey_90 };
        border-left: ${ theme.size.size_5 } solid transparent;
        border-right: ${ theme.size.size_5 } solid transparent;
        height: 0;
        position: absolute;
        top: -${ theme.size.size_5 };
        width: 0;
    ` }

  left: ${ getArrowLeftPosition };
`;

const ArrowLeft = styled.span`
    ${ ({ theme }) => `
        border-bottom: ${ theme.size.size_5 } solid transparent;
        border-left: ${ theme.size.size_5 } solid ${ theme.color.grey.grey_90 };
        border-top: ${ theme.size.size_5 } solid transparent;
        height: 0;
        right: -${ theme.size.size_5 };
        position: absolute;
        top: calc(${ theme.size.size_10 } + ${ theme.size.size_2 });
        width: 0;
    ` }
`;
